import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { FiMenu, FiX } from 'react-icons/fi';

import { RESUME_URL } from '../data/site';

const LINKS = [
  { href: '#skills', label: 'Skills' },
  { href: '#education', label: 'Education' },
  { href: '#experience', label: 'Experience' },
  { href: '#project', label: 'Projects' },
  { href: '#contact', label: 'Contact' },
];

const DELTA_THRESHOLD = 6;
const REVEAL_ZONE = 120;

const getScrollY = () =>
  window.scrollY ||
  document.documentElement.scrollTop ||
  document.body.scrollTop ||
  0;

export default function NewNavbar() {
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState('');
  const [hidden, setHidden] = React.useState(false);

  const lastY = React.useRef(0);
  const holdVisibleUntil = React.useRef(0);
  const pendingHref = React.useRef(null);
  const reduceMotion = useReducedMotion();

  React.useEffect(() => {
    lastY.current = getScrollY();

    const onScroll = () => {
      const y = getScrollY();
      const delta = y - lastY.current;
      lastY.current = y;

      if (open || y < REVEAL_ZONE || Date.now() < holdVisibleUntil.current) {
        setHidden(false);
        return;
      }

      if (Math.abs(delta) < DELTA_THRESHOLD) return;
      setHidden(delta > 0);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('scroll', onScroll, {
      passive: true,
      capture: true,
    });

    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('scroll', onScroll, { capture: true });
    };
  }, [open]);

  const scrollTo = React.useCallback(
    (href) => {
      const el = document.getElementById(href.slice(1));
      if (!el) return;

      // keep the bar on screen while the programmatic scroll runs
      holdVisibleUntil.current = Date.now() + 900;
      setHidden(false);

      el.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start',
      });

      window.history.replaceState(null, '', href);
    },
    [reduceMotion]
  );

  // The mobile panel is in flow, so collapsing it shifts the document up by its
  // full height. Scrolling before that settles leaves the browser chasing a
  // stale offset (and scroll anchoring cancels the smooth scroll outright), so
  // the jump waits until the panel is actually gone.
  const flushPending = React.useCallback(() => {
    const href = pendingHref.current;
    pendingHref.current = null;
    if (href) scrollTo(href);
  }, [scrollTo]);

  const goTo = React.useCallback(
    (e, href) => {
      if (!document.getElementById(href.slice(1))) return;

      e.preventDefault();
      setActive(href);

      if (!open) {
        scrollTo(href);
        return;
      }

      pendingHref.current = href;
      setOpen(false);
      // fallback in case the exit animation never reports completion
      window.setTimeout(flushPending, 420);
    },
    [open, scrollTo, flushPending]
  );

  React.useEffect(() => {
    const ids = LINKS.map((l) => l.href.slice(1));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(`#${e.target.id}`);
        });
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );

    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <motion.nav
      className="nav-x"
      animate={{ y: hidden && !reduceMotion ? '-100%' : '0%' }}
      transition={
        reduceMotion
          ? { duration: 0 }
          : { duration: 0.32, ease: [0.22, 1, 0.36, 1] }
      }
    >
      <div className="nav-x-inner">
        <a
          href="#home"
          className="nav-x-brand"
          onClick={(e) => goTo(e, '#home')}
        >
          Harshit<span>.</span>
        </a>

        <div className="nav-x-links desktop-only">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="nav-x-link"
              onClick={(e) => goTo(e, l.href)}
              style={
                active === l.href
                  ? { color: 'var(--text)', background: 'var(--surface-hover)' }
                  : undefined
              }
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="nav-x-links">
          <a
            href={RESUME_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary-x"
            style={{ padding: '9px 20px', fontSize: '14px' }}
          >
            Resume
          </a>

          <button
            type="button"
            className="nav-x-link mobile-toggle"
            aria-label="Toggle menu"
            aria-expanded={open}
            onClick={() => {
              setHidden(false);
              setOpen((v) => !v);
            }}
          >
            {open ? <FiX /> : <FiMenu />}
          </button>
        </div>
      </div>

      <AnimatePresence onExitComplete={flushPending}>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: 'hidden', borderTop: '1px solid var(--border)' }}
          >
            <div style={{ padding: '12px 24px 18px', display: 'grid', gap: 4 }}>
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  className="nav-x-link"
                  onClick={(e) => goTo(e, l.href)}
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
